import jsPDF from 'jspdf';

const PAGE_MARGIN = 18;
const HEADER_HEIGHT = 30;
const FOOTER_HEIGHT = 16;
const ROW_HEIGHT = 7;
const HEADER_ROW_MIN_HEIGHT = 8;
const FONT_SIZE = 8;
const LINE_HEIGHT = 3.6;
const CELL_PADDING_X = 1.5;
const CELL_PADDING_Y = 1.4;

const toDataUrl = (url) => new Promise((resolve) => {
  if (!url) {
    resolve(null);
    return;
  }
  if (url.startsWith('data:')) {
    resolve(url);
    return;
  }
  const image = new Image();
  image.crossOrigin = 'anonymous';
  image.onload = () => {
    try {
      const canvas = document.createElement('canvas');
      canvas.width = image.width;
      canvas.height = image.height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(image, 0, 0);
      resolve(canvas.toDataURL('image/png'));
    } catch (error) {
      resolve(null);
    }
  };
  image.onerror = () => resolve(null);
  image.src = url;
});

const safeText = (value) => (value === null || value === undefined ? '' : String(value));

const drawLinesLeftMiddle = (doc, lines, x, rowTop, rowHeight) => {
  const safeLines = Array.isArray(lines) && lines.length ? lines : [''];
  const textHeight = safeLines.length * LINE_HEIGHT;
  const blockTop = rowTop + (rowHeight - textHeight) / 2;
  safeLines.forEach((line, index) => {
    const lineCenterY = blockTop + index * LINE_HEIGHT + LINE_HEIGHT / 2;
    doc.text(String(line), x, lineCenterY, { align: 'left', baseline: 'middle' });
  });
};

const drawHeader = (doc, context) => {
  const {
    pageWidth,
    branding,
    metadata,
    logoDataUrl,
  } = context;

  const top = PAGE_MARGIN;
  const left = PAGE_MARGIN;

  // Match shift-log style: left logo, centered title, empty right slot.
  const logoBoxWidth = 34;
  const logoMaxHeight = 12;
  if (logoDataUrl) {
    try {
      const props = doc.getImageProperties(logoDataUrl);
      const ratio = props.width / props.height;
      const width = Math.min(logoBoxWidth, logoMaxHeight * ratio);
      const height = width / ratio;
      doc.addImage(logoDataUrl, 'PNG', left, top, width, height);
    } catch (error) {
      // no-op
    }
  }

  const centerX = pageWidth / 2;
  doc.setFontSize(18);
  doc.setFont(undefined, 'bold');
  doc.text(safeText(metadata.title || branding.headerTitle), centerX, top + 8, { align: 'center' });
  doc.setFontSize(9);
  doc.setFont(undefined, 'normal');
  doc.text(safeText(metadata.subtitle || branding.headerSubtitle), centerX, top + 13, { align: 'center' });
};

const drawFooter = (doc, context) => {
  const {
    pageWidth,
    pageHeight,
    branding,
    metadata,
    footerLogoDataUrl,
    pageNumber,
    totalPages,
  } = context;
  const left = PAGE_MARGIN;
  const right = pageWidth - PAGE_MARGIN;
  const center = pageWidth / 2;
  const baselineY = pageHeight - PAGE_MARGIN + 1;

  let textStartX = left;
  if (footerLogoDataUrl) {
    try {
      const props = doc.getImageProperties(footerLogoDataUrl);
      const ratio = props.width / props.height;
      const height = 5;
      const width = height * ratio;
      doc.addImage(footerLogoDataUrl, 'PNG', left, baselineY - 4.5, width, height);
      textStartX = left + width + 2.5;
    } catch (error) {
      // no-op
    }
  }

  const generatedLabel = safeText(metadata?.generatedAtLabel);
  doc.setFontSize(8);
  doc.text(safeText(branding.footerPoweredBy || branding.footerLeft), textStartX, baselineY);
  if (generatedLabel) {
    doc.setFontSize(7.5);
    doc.text(generatedLabel, center, baselineY, { align: 'center' });
    doc.setFontSize(8);
  }
  doc.text(
    `Page ${pageNumber} of ${totalPages}`,
    right,
    baselineY,
    { align: 'right' }
  );
};

const drawTableHeader = (doc, startX, startY, columnWidths, columns) => {
  const headerLinesByColumn = columns.map((column, index) => {
    const text = safeText(column?.label);
    return doc.splitTextToSize(text, Math.max(1, columnWidths[index] - CELL_PADDING_X * 2));
  });
  const maxLineCount = Math.max(1, ...headerLinesByColumn.map((lines) => lines.length));
  const headerRowHeight = Math.max(
    HEADER_ROW_MIN_HEIGHT,
    maxLineCount * LINE_HEIGHT + CELL_PADDING_Y * 2
  );

  doc.setFont(undefined, 'bold');
  doc.setFillColor(245, 246, 248);
  doc.rect(startX, startY, columnWidths.reduce((sum, w) => sum + w, 0), headerRowHeight, 'F');
  doc.setDrawColor(220, 220, 220);
  doc.line(startX, startY + headerRowHeight, startX + columnWidths.reduce((sum, w) => sum + w, 0), startY + headerRowHeight);
  let cursorX = startX;
  columns.forEach((column, index) => {
    const lines = headerLinesByColumn[index];
    drawLinesLeftMiddle(doc, lines, cursorX + CELL_PADDING_X, startY, headerRowHeight);
    cursorX += columnWidths[index];
  });
  return startY + headerRowHeight;
};

export const buildPdfDocument = async (config, dataset) => {
  const {
    orientation = 'portrait',
    format = 'a4',
    filename = 'report.pdf',
    branding = {},
    metadata = {},
  } = config || {};

  const columns = Array.isArray(dataset?.columns) ? dataset.columns : [];
  const rows = Array.isArray(dataset?.rows) ? dataset.rows : [];

  const doc = new jsPDF({ orientation, format, unit: 'mm' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const tableTop = PAGE_MARGIN + HEADER_HEIGHT;
  const tableBottom = pageHeight - PAGE_MARGIN - FOOTER_HEIGHT;
  const tableWidth = pageWidth - PAGE_MARGIN * 2;
  const columnWidth = columns.length > 0 ? tableWidth / columns.length : tableWidth;
  const columnWidths = columns.map(() => columnWidth);

  const logoDataUrl = await toDataUrl(branding.logo);
  const footerLogoDataUrl = await toDataUrl(branding.footerLogo);
  drawHeader(doc, { pageWidth, branding, metadata, logoDataUrl });

  let y = drawTableHeader(doc, PAGE_MARGIN, tableTop, columnWidths, columns);
  doc.setFont(undefined, 'normal');
  doc.setFontSize(FONT_SIZE);

  rows.forEach((row) => {
    const cellLines = row.map((cell, index) =>
      doc.splitTextToSize(safeText(cell), Math.max(1, columnWidths[index] - CELL_PADDING_X * 2))
    );
    const lineCount = Math.max(1, ...cellLines.map((lines) => lines.length));
    const rowHeight = Math.max(ROW_HEIGHT, lineCount * LINE_HEIGHT + CELL_PADDING_Y * 2);
    if (y + rowHeight > tableBottom) {
      doc.addPage();
      drawHeader(doc, { pageWidth, branding, metadata, logoDataUrl });
      y = drawTableHeader(doc, PAGE_MARGIN, tableTop, columnWidths, columns);
      doc.setFont(undefined, 'normal');
      doc.setFontSize(FONT_SIZE);
    }

    let x = PAGE_MARGIN;
    row.forEach((cell, index) => {
      const lines = cellLines[index];
      drawLinesLeftMiddle(doc, lines, x + CELL_PADDING_X, y, rowHeight);
      x += columnWidths[index];
    });
    doc.setDrawColor(236, 236, 236);
    doc.line(PAGE_MARGIN, y + rowHeight, PAGE_MARGIN + tableWidth, y + rowHeight);
    y += rowHeight;
  });

  const totalPages = doc.internal.getNumberOfPages();
  for (let pageNumber = 1; pageNumber <= totalPages; pageNumber += 1) {
    doc.setPage(pageNumber);
    drawFooter(doc, {
      pageWidth,
      pageHeight,
      branding,
      metadata,
      footerLogoDataUrl,
      pageNumber,
      totalPages,
    });
  }

  const blob = doc.output('blob');
  return { blob, filename };
};

export default buildPdfDocument;
